import { useEffect, useState } from "react";

import { Option } from "@common/components/SelectDropdown/SelectDropdown";
import displayToast from "@common/components/Toast/displayToast";
import { useForm } from "@common/hooks/useForm";
import { automaticForwardOption, IntegrationType, manualForwardOption } from "@common/models/integrations";
import {
  Notion,
  NotionDatabase,
  NotionIntegrationPayload,
  NotionOption,
} from "@common/models/integrations/Notion.model";
import { useIntegrationLayoutSettings } from "@main/pages/Administration/components/ModalBody/UserFeedbackIntegrations/consumers/shared/useIntegrationLayoutSettings";
import { useIntegrationFormConsumer } from "@main/pages/Administration/consumers/useIntegrationFormConsumer";

interface Props {
  integration?: Notion;
  selectedAppId: string;
  onFinish: () => void;
}

interface FormState {
  databaseOption: Option;
  forwardingOption: Option;
}

const initialFormState = {
  databaseOption: { value: "", label: "Fetching... Please wait." },
  forwardingOption: automaticForwardOption,
};

export const useNotionIntegrationFormConsumer = ({ selectedAppId, integration, onFinish }: Props) => {
  const { integrationInfo, integrate, updateIntegration } = useIntegrationFormConsumer<NotionDatabase[]>({
    selectedAppId,
    type: IntegrationType.NOTION,
    toggle: onFinish,
  });

  const {
    integrationLayout,
    moveIntegrationLayoutEntryUp,
    moveIntegrationLayoutEntryDown,
    toggleIntegrationLayoutEntry,
    metadataKeys,
    metadataEnabledState,
    toggleMetadataKey,
    toggleAllMetadataKeys,
    setMetadataKeysEnabled,
    resetMetadataKeys,
    hasMetadataKeyChanges,
  } = useIntegrationLayoutSettings(integration?.integration_layout ?? [], selectedAppId);

  const [loading, setLoading] = useState(false);
  const [databaseOptions, setDatabaseOptions] = useState<Option[]>([]);

  const { formState, handleFormChange, handleFormSubmit } = useForm<FormState>({
    initialState: initialFormState,
    onSubmit: handleConnect,
    validators: {},
  });

  const { databaseOption, forwardingOption } = formState;

  async function handleConnect() {
    setLoading(true);
    try {
      if (!integrationInfo) throw new Error();

      const notionDatabaseInfo = JSON.parse(databaseOption.value) as NotionOption;

      const payload: NotionIntegrationPayload = {
        automatic_forward: !!forwardingOption.value,
        integration_id: notionDatabaseInfo.integrationId,
        database_id: notionDatabaseInfo.databaseId,
        integration_layout: integrationLayout,
      };

      const endpoint = !!integration ? updateIntegration : integrate;

      endpoint<Notion>(payload);
    } catch (error) {
      displayToast({ title: "Something went wrong", content: error.response.data.message });
    } finally {
      setLoading(false);
      onFinish();
    }
  }

  useEffect(() => {
    if (!integrationInfo) return;

    if (!integrationInfo.length) {
      setDatabaseOptions([]);
      handleFormChange("databaseOption", null);
      return;
    }

    const newDatabaseOptions: Option[] = integrationInfo.map((database) => {
      const notionOption: NotionOption = {
        databaseId: database.id,
        integrationId: database.integration_id,
      };
      return { value: JSON.stringify(notionOption), label: database.title };
    });

    setDatabaseOptions([...newDatabaseOptions]);

    const preSelectedDatabase = integration
      ? newDatabaseOptions.find((option) => {
          const notionOption = JSON.parse(option.value) as NotionOption;
          return notionOption.databaseId === integration.database_id;
        }) || null
      : null;

    handleFormChange("databaseOption", preSelectedDatabase);

    const preselectedForwardingOption = integration
      ? integration.automatic_forward
        ? automaticForwardOption
        : manualForwardOption
      : automaticForwardOption;

    handleFormChange("forwardingOption", preselectedForwardingOption);
  }, [integrationInfo, handleFormChange, integration]);

  const disabled = !integrationInfo;

  return {
    disabled,
    loading,
    databaseOptions,
    databaseOption,
    forwardingOption,
    handleFormChange,
    handleFormSubmit,
    integrationLayout,
    moveIntegrationLayoutEntryUp,
    moveIntegrationLayoutEntryDown,
    toggleIntegrationLayoutEntry,
    metadataKeys,
    metadataEnabledState,
    toggleMetadataKey,
    toggleAllMetadataKeys,
    setMetadataKeysEnabled,
    resetMetadataKeys,
    hasMetadataKeyChanges,
  };
};
